/**
 * AuthContextCliente — gestão de sessão do Meu Contador Marçal.
 *
 * Token JWT (30 dias) persistido em localStorage. Multi-dispositivo
 * by design: sem invalidação cross-device, mesma credencial pode
 * logar em N celulares/PCs simultaneamente (cliente + equipe dele).
 *
 * 16/06/2026 — Pacote login automático:
 *  - autoLogin: valida token na inicialização (call /cliente/me)
 *  - lembrarCnpj: persiste CNPJ entre logins (mesmo após logout)
 *  - manterConectado: opt-in pra persistir token. Default true.
 *  - splash: estado `carregando` enquanto valida → ClienteRoute mostra splash
 */
import React, { createContext, useContext, useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { biometriaAtivaQualquer, desativarBiometria } from '../components/cliente/useBiometria';
import { limparTudo as limparCacheLocal } from '../components/cliente/cacheLocal';
import { versaoDoApp, pluginsDeclarados } from '../components/cliente/compartilharBlob';
import { pareceTecnico, FRASE_ERRO_PADRAO } from '../components/cliente/textoTecnico';
import { payloadDoToken, clienteConformeToken } from '../components/cliente/empresaDoToken';

const API_BASE = process.env.REACT_APP_API_URL || '/api';
const STORAGE_TOKEN = 'cliente_token';
const STORAGE_USER = 'cliente_atual';
const STORAGE_CNPJ_LEMBRADO = 'cliente_cnpj_lembrado';
const STORAGE_MANTER_CONECTADO = 'cliente_manter_conectado';

const ClienteAuthContext = createContext(null);

function _authEnviado(config) {
  try {
    const h = config && config.headers;
    if (!h) return '';
    const v = typeof h.get === 'function' ? h.get('Authorization') : (h.Authorization || h.authorization);
    return String(v || '').replace(/^Bearer\s+/i, '');
  } catch (_) { return ''; }
}

export function ClienteAuthProvider({ children }) {
  const [cliente, setCliente] = useState(null);
  const [token, setToken] = useState(null);
  // [2026-10-24] O interceptor lê o token por aqui: assim ele é registrado UMA vez
  // e nunca fica uma janela sem interceptor entre um token e outro (ver abaixo).
  const tokenRef = useRef(null);
  tokenRef.current = token;
  const [carregando, setCarregando] = useState(true);
  // Quando autoLogin + biometria ativa: gate o app até passar o prompt.
  const [bloqueadoPorBiometria, setBloqueadoPorBiometria] = useState(false);

  // Auto-login na inicialização: lê localStorage e valida com /cliente/me
  useEffect(() => {
    let cancelado = false;
    const validar = async () => {
      // Sessão sem "Manter conectado": vale só até o app abrir de novo.
      if (localStorage.getItem(STORAGE_MANTER_CONECTADO) === '0') {
        localStorage.removeItem(STORAGE_TOKEN);
        localStorage.removeItem(STORAGE_USER);
      }
      const tokenSalvo = localStorage.getItem(STORAGE_TOKEN);
      const clienteSalvo = localStorage.getItem(STORAGE_USER);
      if (!tokenSalvo || !clienteSalvo) {
        if (!cancelado) setCarregando(false);
        return;
      }
      try {
        // [Pablo 2026-10-23] A empresa mostrada tem de ser a do token (é nela que
        // a nota sai). Cadastro guardado de outra empresa é corrigido aqui.
        const lido = JSON.parse(clienteSalvo);
        const dados = clienteConformeToken(lido, tokenSalvo);
        if (dados !== lido) localStorage.setItem(STORAGE_USER, JSON.stringify(dados));
        // Otimista: mostra UI primeiro, valida em background.
        // Se /me retornar 401, o interceptor faz logout.
        if (!cancelado) {
          setCliente(dados);
          setToken(tokenSalvo);
          // Se este dispositivo tem biometria ativa, exige desbloqueio antes
          // de liberar o app. RotaClienteProtegida renderiza PromptBiometria.
          if (biometriaAtivaQualquer()) setBloqueadoPorBiometria(true);
          setCarregando(false);
        }
        // Background: confirma sessão real (vai ser exercida na primeira chamada
        // protegida; se token expirou ou foi revogado, interceptor limpa).
      } catch {
        localStorage.removeItem(STORAGE_TOKEN);
        localStorage.removeItem(STORAGE_USER);
        if (!cancelado) setCarregando(false);
      }
    };
    validar();
    return () => { cancelado = true; };
  }, []);

  // Persiste o login. Se manterConectado=false, a sessão some na próxima
  // abertura do app (no refresh, o usuário precisa logar de novo). Quando true
  // (default), sobrevive a reload/fechamento do app.
  const persistirLogin = useCallback((novoToken, dadosCliente, opts = {}) => {
    const { manterConectado = true, lembrarCnpj = true } = opts;
    setToken(novoToken);
    setCliente(dadosCliente);
    // [Bruno 2026-10-01] O token vai pro localStorage SEMPRE: ~40 telas leem dali
    // direto, e sem ele mandavam "Bearer null", o 401 deslogava e o app ficava em
    // branco (Propositiva). "Manter conectado" desmarcado agora só marca a sessão
    // como descartável: o boot (validar, acima) apaga o token na próxima abertura.
    localStorage.setItem(STORAGE_TOKEN, novoToken);
    localStorage.setItem(STORAGE_USER, JSON.stringify(dadosCliente));
    localStorage.setItem(STORAGE_MANTER_CONECTADO, manterConectado ? '1' : '0');
    if (lembrarCnpj && dadosCliente?.cnpj) {
      localStorage.setItem(STORAGE_CNPJ_LEMBRADO, String(dadosCliente.cnpj).replace(/\D/g, ''));
    }
  }, []);

  const logout = useCallback(({ esquecerCnpj = false, esquecerBiometria = false } = {}) => {
    // O último estado das telas fica no aparelho pra a home abrir instantânea —
    // sair da conta tem que levar isso junto, senão o próximo a entrar neste
    // celular veria, por um instante, os números de quem saiu.
    limparCacheLocal();
    localStorage.removeItem(STORAGE_TOKEN);
    localStorage.removeItem(STORAGE_USER);
    if (esquecerCnpj) localStorage.removeItem(STORAGE_CNPJ_LEMBRADO);
    if (esquecerBiometria) desativarBiometria();
    setToken(null);
    setCliente(null);
    setBloqueadoPorBiometria(false);
  }, []);

  const desbloquearBiometria = useCallback(() => setBloqueadoPorBiometria(false), []);

  // Interceptor axios global: 401 em qualquer rota /cliente/* → logout silencioso.
  // useLayoutEffect pelo mesmo motivo do interceptor de pedido (registrado antes dos filhos).
  useLayoutEffect(() => {
    const id = axios.interceptors.response.use(
      (r) => {
        // [Bruno 2026-10-15] RENOVAÇÃO DESLIZANTE. O servidor manda um token novo
        // em `x-novo-token` quando o atual passou de 7 dias (ver authCliente):
        // quem usa o app toda semana nunca é derrubado pelos 30 dias. Só grava se
        // ainda há sessão neste aparelho (logout no meio do pedido não ressuscita).
        try {
          const novo = r?.headers?.['x-novo-token'];
          const url = r?.config?.url || '';
          // [Pablo 2026-10-23] Só aceita a renovação do token que ESTÁ em uso.
          // Um pedido que saiu antes da troca de empresa volta com o token da
          // empresa ANTERIOR renovado; gravar isso deixava a tela dizendo
          // "Coutinho" com o login da Lorca, e a nota saía na Lorca.
          const atual = localStorage.getItem(STORAGE_TOKEN);
          const enviado = _authEnviado(r?.config);
          const mesmaEmpresa = (() => {
            const a = payloadDoToken(atual); const n = payloadDoToken(novo);
            return !!a && !!n && Number(a.cliente_id) === Number(n.cliente_id);
          })();
          if (novo && url.includes('/cliente/') && atual && enviado === atual && mesmaEmpresa) {
            localStorage.setItem(STORAGE_TOKEN, novo);
            setToken(novo);
          }
        } catch (_) { /* renovar nunca atrapalha a resposta */ }
        return r;
      },
      (err) => {
        const status = err.response?.status;
        const url = err.config?.url || '';
        if (status === 401 && url.includes('/cliente/') && !url.includes('/login')) {
          logout();
        }
        // [Pablo 2026-10-23] O servidor recusou porque a empresa da tela não é a
        // do login: a tela passa a mostrar a empresa verdadeira do login.
        if (status === 409 && err.response?.data?.codigo === 'empresa_trocada') {
          try {
            const t = localStorage.getItem(STORAGE_TOKEN);
            const salvo = JSON.parse(localStorage.getItem(STORAGE_USER) || 'null');
            const certo = clienteConformeToken(salvo, t);
            if (certo && certo !== salvo) {
              localStorage.setItem(STORAGE_USER, JSON.stringify(certo));
              setCliente(certo);
            }
          } catch (_) { /* a mensagem do servidor já explica */ }
        }
        // [Auditoria de usabilidade, 2026-08-26] "Network Error" NA CARA DO
        // CLIENTE. Dez telas fazem `err.response?.data?.erro || err.message`, e
        // quando o 4G cai o axios põe em `message` a string dele, em inglês:
        // o cliente lê "Network Error" no lugar de saber que ficou sem sinal.
        // Era em 19 pontos — incluindo as cinco do LOGIN, que é exatamente onde
        // alguém está quando a rede está ruim.
        //
        // Traduzir aqui conserta os dez de uma vez, sem tocar em tela nenhuma.
        // O texto técnico não se perde: fica em `detalheTecnico`, que é o que o
        // reportarErroUi manda pro nosso lado.
        if (!err.response) {
          err.detalheTecnico = err.message;
          err.message = err.code === 'ECONNABORTED'
            ? 'A conexão demorou demais. Tente de novo em um minuto.'
            : 'Sem conexão. Verifique sua internet e tente de novo.';
        } else if (status >= 500 && !err.response?.data?.erro) {
          err.detalheTecnico = err.message;
          err.message = 'O sistema está fora do ar neste momento. Tente daqui a pouco.';
        } else if (err.response?.data && typeof err.response.data.erro === 'string'
          && pareceTecnico(err.response.data.erro)
          // Código snake_case num 4xx é contrato da API (a tela decide a frase,
          // ex. 'possivel_duplicidade'); só no 5xx ele vira frase padrão.
          && (status >= 500 || !/^[a-z0-9]+(_[a-z0-9]+)+$/.test(err.response.data.erro.trim()))) {
          // [Bruno 2026-10-15] Texto de PROGRAMA vindo do servidor ("no such
          // column", "Cannot read properties", código snake_case): as telas
          // leem `response.data.erro` direto, então a troca é aqui, uma vez.
          // O original fica em `detalheTecnico` pro reportarErroUi.
          err.detalheTecnico = err.response.data.erro;
          err.response.data = { ...err.response.data, erro: FRASE_ERRO_PADRAO, codigo: err.response.data.codigo || err.response.data.erro };
          err.message = FRASE_ERRO_PADRAO;
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, [logout]);

  // [Bruno 2026-08-31] O app passa a DIZER qual versão dele está no aparelho.
  //
  // A tela vem do nosso servidor e muda a cada publicação; a casca (o aplicativo
  // da loja) só muda quando sai versão nova. Sem esse número, as duas coisas
  // viravam "o app foi atualizado" e não dava pra saber por que uma correção
  // nativa não tinha chegado. Vai em dois cabeçalhos, pedido a pedido, e o
  // servidor só guarda quando muda.
  const versaoApp = useRef(null);
  // [Bruno 2026-09-03] O QUE A CASCA TEM DENTRO, não só o número dela.
  //
  // O aparelho do cliente da LEW passou de 1.0(2) pra 1.1(3) e o inventário
  // continuou sem responder a única pergunta que importa: a versão nova trouxe o
  // compartilhamento? Só uma queixa nova responderia. Aqui vai a resposta, tirada
  // da lista que o PRÓPRIO APLICATIVO declara (PluginHeaders) — nunca da lista do
  // que o nosso JavaScript importou, que sempre tem os dois e por isso mente.
  const recursosApp = useRef(null);
  // [Pablo 2026-10-23] Versão do sistema do celular (iOS/Android) dita pelo PRÓPRIO
  // aparelho (plugin Device da casca). O user-agent do iPhone congelou a versão em
  // 18.x desde o iOS 26, então só o plugin diz se é o iOS 27. Casca sem o plugin
  // (versões publicadas até 23/10) não manda nada e o servidor cai no user-agent.
  const soApp = useRef(null);
  useEffect(() => {
    let vivo = true;
    versaoDoApp().then((v) => { if (vivo && v) versaoApp.current = v; }).catch(() => {});
    try {
      const dev = typeof window !== 'undefined' && window.Capacitor && window.Capacitor.Plugins
        ? window.Capacitor.Plugins.Device : null;
      if (dev && dev.getInfo) {
        dev.getInfo().then((i) => { if (vivo && i && i.osVersion) soApp.current = String(i.osVersion).slice(0, 16); }).catch(() => {});
      }
    } catch (_) { /* sem o plugin */ }
    try {
      const declarados = pluginsDeclarados();
      if (declarados.length) {
        recursosApp.current = `share=${declarados.includes('Share') ? 1 : 0}`
          + `;arquivos=${declarados.includes('Filesystem') ? 1 : 0}`;
      }
    } catch (_) { /* inventário nunca atrapalha quem está usando o app */ }
    return () => { vivo = false; };
  }, []);

  // Injeta Authorization automaticamente em chamadas /cliente/* (exceto login)
  // [2026-10-24] useLayoutEffect registrado UMA vez (token pelo tokenRef). Antes era
  // useEffect([token]): a cada troca de token o interceptor era removido e refeito,
  // e o React roda os efeitos dos FILHOS antes dos do provider. O AceiteTermos
  // pedia /cliente/termos/situacao nessa janela, sem cabeçalho, levava 401 e o
  // interceptor de resposta deslogava: "entra e volta pro login" (24/10, iOS e Android).
  useLayoutEffect(() => {
    const id = axios.interceptors.request.use((config) => {
      const url = config.url || '';
      // [Bruno 2026-10-23] Logo depois do login, as telas filhas (AceiteTermos)
      // disparam pedidos ANTES deste efeito ser refeito com o token novo: o
      // interceptor antigo (token null) mandava sem cabeçalho, o 401 deslogava
      // e o cliente "não entrava". O persistirLogin já gravou o token no
      // localStorage nessa hora, então ele é a reserva.
      let tokenAtual = tokenRef.current;
      if (!tokenAtual) {
        try { tokenAtual = localStorage.getItem(STORAGE_TOKEN); } catch (_) { tokenAtual = null; }
      }
      if (tokenAtual && url.includes('/cliente/') && !url.includes('/login')) {
        config.headers = config.headers || {};
        if (!config.headers.Authorization) {
          config.headers.Authorization = `Bearer ${tokenAtual}`;
        }
        const cap = typeof window !== 'undefined' ? window.Capacitor : null;
        // [2026-10-23] A plataforma vai sempre (alerta de queda por plataforma):
        // antes da versão chegar, o app do iPhone contaria como navegador.
        config.headers['X-App-Plataforma'] = cap?.getPlatform ? cap.getPlatform() : 'web';
        // [Pablo 2026-10-23] A empresa que a TELA mostra. O servidor recusa
        // emitir/cancelar nota se ela não for a do login (nota na empresa errada).
        try {
          const salvo = JSON.parse(localStorage.getItem(STORAGE_USER) || 'null');
          if (salvo && salvo.id) config.headers['X-Empresa-Tela'] = String(salvo.id);
        } catch (_) { /* sem a conferência, o servidor segue como antes */ }
        if (versaoApp.current) {
          config.headers['X-App-Versao'] = versaoApp.current;
          if (recursosApp.current) config.headers['X-App-Recursos'] = recursosApp.current;
          if (soApp.current) config.headers['X-App-SO'] = soApp.current;
        }
      }
      return config;
    });
    return () => axios.interceptors.request.eject(id);
  }, []);

  const cnpjLembrado = localStorage.getItem(STORAGE_CNPJ_LEMBRADO) || '';
  const manterConectadoPref = localStorage.getItem(STORAGE_MANTER_CONECTADO);
  // default true se nunca foi setado
  const manterConectadoDefault = manterConectadoPref == null ? true : manterConectadoPref === '1';

  return (
    <ClienteAuthContext.Provider value={{
      cliente, token, carregando,
      // [Bruno 2026-09-10] O redesenho atualiza `app_v2`/`regime` no cliente
      // depois de perguntar ao /me (useAppV2). Exposto pra isso, e só pra isso.
      setCliente,
      autenticado: !!token && !!cliente,
      bloqueadoPorBiometria, desbloquearBiometria,
      persistirLogin, logout,
      cnpjLembrado, manterConectadoDefault,
    }}>
      {children}
    </ClienteAuthContext.Provider>
  );
}

export function useClienteAuth() {
  const ctx = useContext(ClienteAuthContext);
  if (!ctx) throw new Error('useClienteAuth deve ser usado dentro de ClienteAuthProvider');
  return ctx;
}
